const ACTION_INJECTION = 'INJECTION';
const ACTION_FAMILY_PLANNING = 'FAMILY_PLANNING';
const ACTION_CLOSURE = 'CLOSURE';

export function normalizeActLabel(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

export function catalogNameMatches(catalogName, storedDescription) {
  const name = normalizeActLabel(catalogName);
  const desc = normalizeActLabel(storedDescription);
  if (!name || !desc) return false;
  if (desc === name) return true;
  for (const prefix of ['injection ', 'planning familial ']) {
    if (desc === `${prefix}${name}`) return true;
  }
  return false;
}

function actionKind(action) {
  return String(action?.action_type || '').trim().toUpperCase();
}

export function isInjectionAction(action) {
  return actionKind(action) === ACTION_INJECTION;
}

export function isFamilyPlanningAction(action) {
  return actionKind(action) === ACTION_FAMILY_PLANNING;
}

export function isNursingCareAction(action) {
  const type = actionKind(action);
  return type !== ACTION_INJECTION && type !== ACTION_FAMILY_PLANNING && type !== ACTION_CLOSURE;
}

export function isManualCareAction(action) {
  if (!action || action.catalog_item_id) return false;
  return isNursingCareAction(action);
}

export function countCareActionsByKind(careActions = []) {
  return {
    care: careActions.filter(isNursingCareAction).length,
    injection: careActions.filter(isInjectionAction).length,
    family_planning: careActions.filter(isFamilyPlanningAction).length
  };
}

export function catalogItemKey(kind, id) {
  if (kind === 'injection') return `inj:${id}`;
  if (kind === 'family_planning') return `pf:${id}`;
  return String(id);
}

export function recordedSelectionForKind(kind, careActions, catalog = []) {
  if (kind === 'injection') {
    const next = new Set();
    catalog.forEach((item) => {
      if (isInjectionCatalogItemRecorded(item, careActions)) next.add(catalogItemKey(kind, item.id));
    });
    return next;
  }
  if (kind === 'family_planning') {
    const next = new Set();
    catalog.forEach((item) => {
      if (isFamilyPlanningCatalogItemRecorded(item, careActions)) {
        next.add(catalogItemKey(kind, item.id));
      }
    });
    return next;
  }
  return recordedCareCatalogIds(careActions);
}

export function recordedCareCatalogIds(careActions) {
  const ids = new Set();
  (careActions || []).forEach((action) => {
    if (action.catalog_item_id != null && String(action.catalog_item_id).trim() !== '') {
      ids.add(String(action.catalog_item_id));
    }
  });
  return ids;
}

function isCatalogLinkedAction(action, { idField, matchesKind, catalogId, catalogName }) {
  if (!matchesKind(action)) return false;
  if (action[idField] != null && String(action[idField]).trim() !== '') {
    return String(action[idField]) === String(catalogId);
  }
  return catalogNameMatches(catalogName, action.description);
}

export function isInjectionCatalogItemRecorded(item, careActions) {
  return (careActions || []).some((action) =>
    isCatalogLinkedAction(action, {
      idField: 'injection_catalog_item_id',
      matchesKind: isInjectionAction,
      catalogId: item?.id,
      catalogName: item?.name
    })
  );
}

export function isFamilyPlanningCatalogItemRecorded(item, careActions) {
  return (careActions || []).some((action) =>
    isCatalogLinkedAction(action, {
      idField: 'family_planning_catalog_item_id',
      matchesKind: isFamilyPlanningAction,
      catalogId: item?.id,
      catalogName: item?.name
    })
  );
}

export function buildRecordedSelection(careActions, injectionCatalog = [], familyPlanningCatalog = []) {
  const next = new Set(recordedCareCatalogIds(careActions));
  (injectionCatalog || []).forEach((item) => {
    if (isInjectionCatalogItemRecorded(item, careActions)) next.add(`inj:${item.id}`);
  });
  (familyPlanningCatalog || []).forEach((item) => {
    if (isFamilyPlanningCatalogItemRecorded(item, careActions)) next.add(`pf:${item.id}`);
  });
  return next;
}

export function careActionDisplayTitle(action) {
  return (
    action?.catalog_name ||
    action?.injection_catalog_name ||
    action?.family_planning_catalog_name ||
    action?.description ||
    action?.action_type ||
    'Soin'
  );
}
